import axios from "axios"
import React, { useContext, useState } from "react"

// components
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from "@material-ui/core"

// types and contexts
import { FeedbackContext } from "../../../context/FeedbackContext"
import { GlobalsContext } from "../../../context/GlobalsContext"

interface Props {
	open: boolean
	close: () => void
	refetchTeams: () => void
}

interface FormValues {
	name: string
	general_notes: string
}

const initialFormValues: FormValues = {
	name: "",
	general_notes: "",
}

export const NewTeamModal = ({ open, close, refetchTeams }: Props) => {
	const globals = useContext(GlobalsContext)
	const { setFeedback } = useContext(FeedbackContext)

	const [loading, setLoading] = useState<boolean>(false)
	const [formValues, setFormValues] = useState<FormValues>(initialFormValues)

	const handleInputChange = (field: keyof FormValues) => (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setFormValues({
			...formValues,
			[field]: event.target.value,
		})
	}

	const resetForm = () => {
		setFormValues(initialFormValues)
	}

	const handleClose = () => {
		resetForm()
		close()
	}

	const createTeam = async () => {
		// Validate required fields
		if (!formValues.name.trim()) {
			setFeedback({
				show: true,
				severity: "warning",
				message: "Please enter a team name",
			})
			return
		}

		setLoading(true)
		try {
			await axios.post(
				"/api/team",
				{
					name: formValues.name.trim(),
					general_notes: formValues.general_notes.trim(),
					favorite: false,
				},
				globals.apiHeader
			)

			setFeedback({
				show: true,
				severity: "success",
				message: "Team created successfully!",
			})
			handleClose()
			refetchTeams()
		} catch (e: any) {
			console.error("Error creating team:", e)
			const errorMessage =
				e.response?.data || "Something went wrong creating the team"
			setFeedback({
				show: true,
				severity: "error",
				message: errorMessage,
			})
		}
		setLoading(false)
	}

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Add New Team</DialogTitle>
			<DialogContent>
				<TextField
					autoFocus
					margin="dense"
					label="Team Name"
					fullWidth
					required
					value={formValues.name}
					onChange={handleInputChange("name")}
					disabled={loading}
				/>
				<TextField
					margin="dense"
					label="General Notes (Optional)"
					fullWidth
					multiline
					rows={3}
					value={formValues.general_notes}
					onChange={handleInputChange("general_notes")}
					disabled={loading}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={createTeam}
					color="primary"
					variant="contained"
					disabled={loading}
				>
					{loading ? <CircularProgress size={20} /> : "Create Team"}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
